/* eslint-disable react/no-unstable-nested-components */
import classnames from 'classnames';
import { memo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import './styles.scss';
import { ProgressLoading, OverlayLoading } from '@/shared/ui';
import FallbackError from '@/features/reload-report-tab';
import { DataEffect, UIEffect } from '@/composers';
import HRSummaryModal from './modal';
import WrapReport from './wrapReport';
import SummaryFunction from './summaryFunction';
import useHrSummaryTab from './useHrSummaryTab';

export type HrSummaryTabProps = {
  activeTab?: any;
  tabRef?: any;
};

/**
 * Top-level HR summary tab, gating the real content behind AI processing, IndexedDB load and beat download. Until those complete it shows the progress loader beside the summary function bar; afterwards it wraps the tab in an error boundary, overlays a loader while the tab is inactive, and mounts the data and UI effect components, the HR summary modal and the report body.
 *
 * @param props - Tab props carrying the active tab id and the ref forwarded to the summary function bar.
 * @returns The rendered HR summary tab, or its loading state while prerequisites are pending.
 */
const HrSummaryTab = ({ activeTab, tabRef }: HrSummaryTabProps) => {
  const {
    keyRecoil,
    isStartedAiProcessing,
    isShowTabHeader,
    studyId,
    profileId,
    isNotEdit,
    isLoadedIndexDB,
    isCompletedDownloadBeat,
    isTabActive,
    hasVisitTab,
  } = useHrSummaryTab({ activeTab });

  if (
    !isStartedAiProcessing ||
    !hasVisitTab.current ||
    !isLoadedIndexDB ||
    !isCompletedDownloadBeat
  ) {
    return (
      <>
        <ProgressLoading isStartedAiProcessing={isStartedAiProcessing} />
        <SummaryFunction
          ref={tabRef}
          profileId={profileId}
          studyId={studyId}
          isNotEdit={isNotEdit}
        />
      </>
    );
  }

  return (
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => (
        <FallbackError
          error={error}
          resetErrorBoundary={resetErrorBoundary}
          isShowTabHeader={isShowTabHeader}
        />
      )}
    >
      <div className={classnames('hr-summary-tab', isShowTabHeader ? '' : '--hide-tab')}>
        <OverlayLoading isVisible={!isTabActive} />
        {isTabActive && (
          <>
            <DataEffect tab={activeTab} keyRecoil={keyRecoil} />
            <UIEffect tab={activeTab} keyRecoil={keyRecoil} />

            <HRSummaryModal />
            <SummaryFunction
              ref={tabRef}
              profileId={profileId}
              studyId={studyId}
              isNotEdit={isNotEdit}
            />
            <WrapReport />
          </>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default memo(HrSummaryTab);
